import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';

// --- CONFIGURACIÓN ---
// NOTA: Reemplaza esta URL con la URL base de tu API.
import { API_BASE_URL } from '../App';

// --- Material-UI Imports ---
import {
    Container, Box, Button, Paper, TextField, Typography, Stack,
    CircularProgress, Alert, Tooltip, IconButton, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, FormControl, InputLabel,
    Select, MenuItem, Switch, InputAdornment,
    Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
    Snackbar, Autocomplete, createFilterOptions, FilterOptionsState
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SearchIcon from '@mui/icons-material/Search';
import LocalOfferIcon from '@mui/icons-material/LocalOffer'; // <-- ÍCONO AÑADIDO

// --- INTERFACES ---
interface CPANetwork {
    id: number;
    name: string;
}

interface Offer {
    id: number;
    name: string;
    url: string;
    payout: number | string;
    status: 'active' | 'inactive';
    cpa_network_id: number;
    cpa_network_name?: string;
    created_at: string;
    updated_at: string;
}

interface AddNewOption {
    inputValue: string;
    label: string;
}
type CpaOptionType = CPANetwork | AddNewOption;

// --- Custom Confirmation Dialog Component ---
const ConfirmationDialog: React.FC<{
    open: boolean;
    title: string;
    message: string;
    onClose: () => void;
    onConfirm: () => void;
}> = ({ open, title, message, onClose, onConfirm }) => (
    <Dialog open={open} onClose={onClose}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent><DialogContentText color="text.secondary">{message}</DialogContentText></DialogContent>
        <DialogActions>
            <Button onClick={onClose}>Cancelar</Button>
            <Button onClick={onConfirm} color="error" variant="contained">Confirmar</Button>
        </DialogActions>
    </Dialog>
);

// --- Custom Clone Offer Dialog Component ---
const CloneOfferDialog: React.FC<{
    open: boolean;
    offerName: string;
    onClose: () => void;
    onConfirm: (newName: string) => void;
    error: string | null;
}> = ({ open, offerName, onClose, onConfirm, error }) => {
    const [nameInput, setNameInput] = useState<string>('');

    useEffect(() => {
        if (open) {
            setNameInput(`Copia de ${offerName}`);
        }
    }, [open, offerName]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Clonar Oferta</DialogTitle>
            <DialogContent>
                <DialogContentText color="text.secondary" sx={{ mb: 2 }}>
                    Introduce un nuevo nombre para la oferta clonada.
                </DialogContentText>
                <TextField autoFocus margin="dense" label="Nuevo Nombre de la Oferta" type="text" fullWidth variant="outlined" value={nameInput} onChange={(e) => setNameInput(e.target.value)} error={!!error} helperText={error} />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <Button onClick={() => onConfirm(nameInput)} variant="contained" disabled={!nameInput}>Clonar</Button>
            </DialogActions>
        </Dialog>
    );
};


// --- COMPONENTE PRINCIPAL ---
const OffersPage: React.FC = () => {
    // --- ESTADOS ---
    const [offers, setOffers] = useState<Offer[]>([]);
    const [cpaNetworks, setCpaNetworks] = useState<CPANetwork[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [notification, setNotification] = useState<{ type: 'success' | 'error', message: string } | null>(null);
    const [editingOfferId, setEditingOfferId] = useState<number | null>(null);

    // Estados del formulario
    const [offerName, setOfferName] = useState<string>('');
    const [offerUrl, setOfferUrl] = useState<string>('');
    const [offerPayout, setOfferPayout] = useState<string>('0.00');
    const [selectedCpaNetworkId, setSelectedCpaNetworkId] = useState<number | ''>('');
    const [formError, setFormError] = useState<boolean>(false); // Estado para controlar errores de validación del formulario

    // Estados de la tabla y diálogos
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);
    const [offerToDelete, setOfferToDelete] = useState<Offer | null>(null);

    const [openCloneDialog, setOpenCloneDialog] = useState<boolean>(false);
    const [offerToClone, setOfferToClone] = useState<Offer | null>(null);
    const [cloneDialogError, setCloneDialogError] = useState<string | null>(null);

    const [addCpaDialogOpen, setAddCpaDialogOpen] = useState(false);
    const [newCpaNetworkName, setNewCpaNetworkName] = useState('');

    const theme = useTheme();
    const headerCellStyle = { fontWeight: 'bold', backgroundColor: theme.palette.action.hover };

    const fetchData = useCallback(async (newlyCreatedNetworkId?: number) => {
        if (offers.length === 0) setIsLoading(true);
        setError(null);
        try {
            const [offersResponse, cpaNetworksResponse] = await Promise.all([
                axios.get(`${API_BASE_URL}/offers`),
                axios.get(`${API_BASE_URL}/cpa-networks`)
            ]);

            const cpaNetworksData = cpaNetworksResponse.data;
            const offersWithNetworkNames = offersResponse.data.map((offer: Offer) => ({
                ...offer,
                cpa_network_name: cpaNetworksData.find((network: CPANetwork) => network.id === offer.cpa_network_id)?.name || 'N/A'
            }));
            setOffers(offersWithNetworkNames);
            setCpaNetworks(cpaNetworksData);
            
            if (newlyCreatedNetworkId) {
                setSelectedCpaNetworkId(newlyCreatedNetworkId);
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Error desconocido al cargar datos.');
        } finally {
            setIsLoading(false);
        }
    }, [offers.length]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const resetForm = () => {
        setEditingOfferId(null);
        setOfferName('');
        setOfferUrl('');
        setOfferPayout('0.00');
        setSelectedCpaNetworkId('');
        setNotification(null);
        setFormError(false);
    };

    const startEditing = (offer: Offer) => {
        setEditingOfferId(offer.id);
        setOfferName(offer.name);
        setOfferUrl(offer.url);
        setOfferPayout(parseFloat(String(offer.payout)).toFixed(2));
        setSelectedCpaNetworkId(offer.cpa_network_id);
        setFormError(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setFormError(false); 

        const isPayoutInvalid = parseFloat(offerPayout) < 0 || isNaN(parseFloat(offerPayout));

        if (!offerName || !offerUrl || !selectedCpaNetworkId || isPayoutInvalid) {
            setNotification({type: 'error', message: "Por favor, completa todos los campos obligatorios correctamente."});
            setFormError(true);
            return;
        }

        const offerData = {
            name: offerName,
            url: offerUrl,
            payout: parseFloat(offerPayout),
            cpa_network_id: Number(selectedCpaNetworkId),
        };

        const endpoint = editingOfferId ? `/offers/${editingOfferId}` : '/offers';
        const method = editingOfferId ? 'put' : 'post';

        try {
            await axios({ method, url: `${API_BASE_URL}${endpoint}`, data: offerData });
            setNotification({ type: 'success', message: `Oferta ${editingOfferId ? 'actualizada' : 'creada'} con éxito.` });
            resetForm();
            fetchData();
        } catch (err: any) {
            setNotification({ type: 'error', message: err.response?.data?.message || 'Error desconocido al guardar la oferta.' });
        }
    };
    
    const handleAddNewCpaNetwork = async () => {
        if (!newCpaNetworkName.trim()) return;
        try {
            const response = await axios.post(`${API_BASE_URL}/cpa-networks`, { name: newCpaNetworkName });
            const newNetwork = response.data;
            setNotification({ type: 'success', message: `Red CPA "${newNetwork.name}" creada.` });
            setAddCpaDialogOpen(false);
            setNewCpaNetworkName('');
            await fetchData(newNetwork.id);
        } catch (err: any) {
            setNotification({ type: 'error', message: err.response?.data?.message || 'Error al crear la Red CPA.' });
        }
    };
    
    const handleDeleteClick = (offer: Offer) => { setOfferToDelete(offer); setOpenConfirmDialog(true); };
    const handleConfirmDelete = async () => { if (offerToDelete) { setOpenConfirmDialog(false); try { await axios.delete(`${API_BASE_URL}/offers/${offerToDelete.id}`); setNotification({ type: 'success', message: `Oferta (ID: ${offerToDelete.id}) eliminada con éxito.` }); if (editingOfferId === offerToDelete.id) resetForm(); fetchData(); } catch (err: any) { setNotification({type: 'error', message: err.response?.data?.message || 'No se pudo eliminar la oferta.'}); } finally { setOfferToDelete(null); } } };
    const handleCancelDelete = () => { setOpenConfirmDialog(false); setOfferToDelete(null); };
    const handleCloneClick = (offer: Offer) => { setOfferToClone(offer); setCloneDialogError(null); setOpenCloneDialog(true); };
    const handleConfirmClone = async (newName: string) => { if (!newName.trim()) { setCloneDialogError("El nombre no puede estar vacío."); return; } if (offerToClone) { setOpenCloneDialog(false); try { const cloneData = { name: newName, url: offerToClone.url, payout: parseFloat(String(offerToClone.payout)), cpa_network_id: offerToClone.cpa_network_id, status: offerToClone.status }; await axios.post(`${API_BASE_URL}/offers`, cloneData); setNotification({ type: 'success', message: `Oferta '${offerToClone.name}' clonada con éxito como '${newName}'.` }); fetchData(); } catch (err: any) { setNotification({type: 'error', message: err.response?.data?.message || 'No se pudo clonar la oferta.'}); } finally { setOfferToClone(null); } } };
    const handleCancelClone = () => { setOpenCloneDialog(false); setOfferToClone(null); setCloneDialogError(null); };
    const handleStatusToggle = async (offerToUpdate: Offer) => { const newStatus = offerToUpdate.status === 'active' ? 'inactive' : 'active'; try { await axios.put(`${API_BASE_URL}/offers/${offerToUpdate.id}`, { status: newStatus }); setNotification({ type: 'success', message: `Estado de la oferta '${offerToUpdate.name}' actualizado.` }); setOffers(prev => prev.map(o => o.id === offerToUpdate.id ? { ...o, status: newStatus } : o) ); } catch (err: any) { setNotification({type: 'error', message: err.response?.data?.message || 'No se pudo actualizar el estado.'}); } };

    const filteredOffers = useMemo(() => {
        if (!searchTerm) return offers;
        const lowercasedFilter = searchTerm.toLowerCase();
        return offers.filter(offer =>
            String(offer.id).includes(lowercasedFilter) ||
            offer.name.toLowerCase().includes(lowercasedFilter) ||
            offer.url.toLowerCase().includes(lowercasedFilter) ||
            (offer.cpa_network_name || '').toLowerCase().includes(lowercasedFilter) ||
            offer.status.toLowerCase().includes(lowercasedFilter)
        );
    }, [offers, searchTerm]);

    // CORRECCIÓN: Se renombra la función para evitar shadowing y confusiones.
    const baseFilterOptions = createFilterOptions<CpaOptionType>();

    if (isLoading && offers.length === 0) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
    if (error && cpaNetworks.length === 0) return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>;

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Snackbar open={!!notification} autoHideDuration={6000} onClose={() => setNotification(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setNotification(null)} severity={notification?.type} sx={{ width: '100%' }}>{notification?.message}</Alert>
            </Snackbar>

            <ConfirmationDialog open={openConfirmDialog} title="Confirmar Eliminación" message={`¿Estás seguro de que quieres eliminar la oferta "${offerToDelete?.name}" (ID: ${offerToDelete?.id})? Esta acción no se puede deshacer.`} onClose={handleCancelDelete} onConfirm={handleConfirmDelete}/>
            
            <CloneOfferDialog open={openCloneDialog} offerName={offerToClone?.name || ''} onClose={handleCancelClone} onConfirm={handleConfirmClone} error={cloneDialogError}/>
            
            <Dialog open={addCpaDialogOpen} onClose={() => setAddCpaDialogOpen(false)}>
                <DialogTitle>Añadir Nueva Red CPA</DialogTitle>
                <DialogContent><TextField autoFocus margin="dense" label="Nombre de la Red CPA" type="text" fullWidth variant="standard" value={newCpaNetworkName} onChange={(e) => setNewCpaNetworkName(e.target.value)} /></DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddCpaDialogOpen(false)}>Cancelar</Button>
                    <Button onClick={handleAddNewCpaNetwork}>Guardar</Button>
                </DialogActions>
            </Dialog>

            {/* --- CAMBIO: Título actualizado --- */}
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                <LocalOfferIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: '2.5rem' }} />
                Gestión de Ofertas
            </Typography>

            <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, mb: 4, borderRadius: '12px' }}>
                <Typography variant="h6" component="h2" gutterBottom color="text.primary">
                    {editingOfferId ? 'Editar Oferta' : 'Crear Nueva Oferta'}
                </Typography>
                <Box component="form" onSubmit={handleSubmit} noValidate>
                    <Stack spacing={3}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                            <TextField fullWidth label="Nombre de la Oferta" value={offerName} onChange={e => setOfferName(e.target.value)} required error={formError && !offerName} />
                            
                            {/* CORRECCIÓN: Se especifica el tipo genérico para Autocomplete para resolver el error de TS. */}
                            <Autocomplete<CpaOptionType, false, false, true>
                                fullWidth
                                freeSolo
                                value={cpaNetworks.find(network => network.id === selectedCpaNetworkId) || null}
                                onChange={(event, newValue) => {
                                    if (typeof newValue === 'string') {
                                        setNewCpaNetworkName(newValue);
                                        setAddCpaDialogOpen(true);
                                    } else if (newValue && 'inputValue' in newValue) {
                                        setNewCpaNetworkName(newValue.inputValue);
                                        setAddCpaDialogOpen(true);
                                    } else if(newValue) {
                                        setSelectedCpaNetworkId(newValue.id);
                                    } else {
                                        setSelectedCpaNetworkId('');
                                    }
                                }}
                                filterOptions={(options, params) => {
                                    // CORRECCIÓN: Se usa la función base y se mejora la lógica de filtrado.
                                    const filtered = baseFilterOptions(options, params);
                                    const isExisting = cpaNetworks.some((option) => params.inputValue === option.name);
                                    if (params.inputValue !== '' && !isExisting) {
                                        filtered.push({
                                            inputValue: params.inputValue,
                                            label: `Añadir "${params.inputValue}"`,
                                        });
                                    }
                                    return filtered;
                                }}
                                options={cpaNetworks}
                                getOptionLabel={(option) => {
                                    if (typeof option === 'string') return option;
                                    if ('label' in option) return option.label;
                                    return option.name;
                                }}
                                renderInput={(params) => <TextField {...params} label="Red CPA" required error={formError && !selectedCpaNetworkId} />}
                            />
                        </Stack>

                        <TextField fullWidth label="URL de la Oferta" type="url" value={offerUrl} onChange={e => setOfferUrl(e.target.value)} required placeholder="https://tuaferta.com/?subid={clickid}&payout={payout}" error={formError && !offerUrl} InputProps={{endAdornment: (<InputAdornment position="end"><Tooltip title="Previsualizar URL"><IconButton component="a" href={offerUrl || '#'} target="_blank" rel="noopener noreferrer" disabled={!offerUrl} edge="end" color="primary"><VisibilityIcon /></IconButton></Tooltip></InputAdornment>)}}/>
                        <TextField label="Payout" type="number" inputProps={{ step: "0.01" }} value={offerPayout} onChange={e => setOfferPayout(e.target.value)} required sx={{ maxWidth: '200px' }} error={formError && (parseFloat(offerPayout) < 0 || isNaN(parseFloat(offerPayout)))} />

                        <Stack direction="row" spacing={2}>
                            <Button type="submit" variant="contained">{editingOfferId ? 'Actualizar Oferta' : 'Crear Oferta'}</Button>
                            {editingOfferId && <Button variant="text" onClick={resetForm}>Cancelar</Button>}
                        </Stack>
                    </Stack>
                </Box>
            </Paper>

            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" spacing={2} sx={{ mt: 5, mb: 2 }}>
                <Typography variant="h5" component="h2" color="text.primary">Ofertas Registradas</Typography>
                <TextField size="small" variant="outlined" placeholder="Buscar por ID, Nombre, URL, Red CPA, Estado..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}/>
            </Stack>

            <Paper elevation={3} sx={{ overflow: 'hidden', borderRadius: '12px' }}>
                <TableContainer>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={headerCellStyle}>ID</TableCell>
                                <TableCell sx={headerCellStyle}>Nombre</TableCell>
                                <TableCell sx={headerCellStyle}>Red CPA</TableCell>
                                <TableCell sx={headerCellStyle}>Payout</TableCell>
                                <TableCell sx={headerCellStyle}>Estado</TableCell>
                                <TableCell align="right" sx={headerCellStyle}>Acciones</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredOffers.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 3 }}>
                                        No se encontraron ofertas.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredOffers.map((offer) => (
                                    <TableRow key={offer.id} hover>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{offer.id}</TableCell>
                                        <TableCell sx={{ textTransform: 'none' }}>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{offer.name}</Typography>
                                            <Tooltip title={offer.url}>
                                                <Typography variant="caption" color="text.secondary" sx={{wordBreak: 'break-all', cursor: 'pointer', display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflow: 'hidden', textOverflow: 'ellipsis' }}>{offer.url}</Typography>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{offer.cpa_network_name || 'N/A'}</TableCell>
                                        <TableCell>${parseFloat(String(offer.payout)).toFixed(2)}</TableCell>
                                        <TableCell>
                                            <Tooltip title={`Cambiar a ${offer.status === 'active' ? 'inactiva' : 'activa'}`}>
                                                <Switch checked={offer.status === 'active'} onChange={() => handleStatusToggle(offer)} color="success" inputProps={{ 'aria-label': 'toggle offer status' }}/>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Previsualizar URL"><IconButton component="a" href={offer.url} target="_blank" rel="noopener noreferrer" size="small" color="primary"><VisibilityIcon /></IconButton></Tooltip>
                                            <Tooltip title="Clonar"><IconButton size="small" onClick={() => handleCloneClick(offer)}><ContentCopyIcon /></IconButton></Tooltip>
                                            <Tooltip title="Editar"><IconButton size="small" onClick={() => startEditing(offer)}><EditIcon /></IconButton></Tooltip>
                                            <Tooltip title="Eliminar"><IconButton size="small" color="error" onClick={() => handleDeleteClick(offer)}><DeleteForeverIcon /></IconButton></Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Container>
    );
};

export default OffersPage;